import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    FormLayout,
    TextField,
    Checkbox,
    ChoiceList,
    Select,
    Autocomplete,
    Icon,
    Stack,
    Tag
} from '@shopify/polaris';
import {
    SearchMinor
} from '@shopify/polaris-icons';
import { apiFetch as fetch } from '../../../../common/Helpers';

export function TagProductsWhenMultipleTagsExistSettings(props) {

    const [availableTags, setAvailableTags] = useState([]);
    const [matchingOptions, setMatchingOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        fetch(`api/products/tags`, { method: 'GET' })
            .then(response => response.json())
            .then(data => {
                setAvailableTags(data);
                setMatchingOptions(data.map(item => ({ label: item, value: item })));
            });
    }, []);

    const updateText = useCallback((value) => {
        setInputValue(value);

        if (value === '') {
            setMatchingOptions(availableTags.map(item => ({ label: item, value: item })));
            return;
        }

        const filterRegex = new RegExp(value, 'i');
        const filtered = availableTags.filter(option => option.match(filterRegex));
        var options = filtered.map(item => ({ label: item, value: item }));

        var exactMatch = availableTags.some(tag => tag.toLowerCase() === value.toLowerCase());
        if (!exactMatch && value.trim() !== '') {
            options.unshift({ label: `Add "${value.trim()}"`, value: value.trim() });
        }

        setMatchingOptions(options);
    }, [availableTags]);

    const handleTagSelected = (selected) => {
        if (selected.length <= 0) {
            return;
        }

        var selectedTag = selected[0];
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        var currentTags = newSettings.requiredTags || [];
        var existingIndex = currentTags.findIndex(tag => tag === selectedTag);

        if (existingIndex >= 0) {
            currentTags.splice(existingIndex, 1);
        } else {
            currentTags.push(selectedTag);
        }

        newSettings.requiredTags = currentTags;
        props.onSettingsChange(newSettings);
        setInputValue('');
    }

    const handleTagRemoved = (tag) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.requiredTags = (newSettings.requiredTags || []).filter(t => t !== tag);
        props.onSettingsChange(newSettings);
    }

    const onTagMatchConditionChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.tagMatchCondition = value;
        props.onSettingsChange(newSettings);
    }

    const onTagToAddChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.tagsToAdd = [value];
        props.onSettingsChange(newSettings);
    }

    const onRemoveTagsIfRequiredTagIsMissingChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.removeTagsIfRequiredTagIsMissing = value;
        props.onSettingsChange(newSettings);
    }

    const onSkipAddTagsIfAlreadyAddedChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.skipAddTagsIfAlreadyAdded = value;
        props.onSettingsChange(newSettings);
    }

    const onProductStatusesChange = (value) => {
        var newSettings = JSON.parse(JSON.stringify(props.settings));
        newSettings.productStatuses = value;
        props.onSettingsChange(newSettings);
    }

    const tagMatchConditionOptions = [
        { label: 'All - All required tags must exist', value: 'All' },
        { label: 'Any - At least one required tag must exist', value: 'Any' },
        { label: 'None - None of the required tags must exist', value: 'None' },
    ];

    const textField = (
        <Autocomplete.TextField
            onChange={updateText}
            label="Required tags"
            value={inputValue}
            prefix={<Icon source={SearchMinor} color="inkLighter" />}
            placeholder="Search for tags or type a new one and press Enter"
            error={props.settingsErrors["requiredTags"]}
            helpText={
                <span>
                    Search and select existing tags, or type a new tag name and press Enter to add it.
                </span>
            }
        />
    );

    return (
        <React.Fragment>
            <Card.Section>
                <FormLayout>
                    <Autocomplete
                        options={matchingOptions}
                        selected={props.settings.requiredTags || []}
                        onSelect={handleTagSelected}
                        textField={textField}
                    />
                    <Stack>
                        {(props.settings.requiredTags || []).map(item =>
                            <Tag key={item} onRemove={() => handleTagRemoved(item)}>{item}</Tag>
                        )}
                    </Stack>
                    <Select
                        label="Tag match condition"
                        options={tagMatchConditionOptions}
                        value={props.settings.tagMatchCondition}
                        onChange={onTagMatchConditionChange}
                        helpText={
                            <span>
                                Determines how the required tags are matched against the product's tags.
                            </span>
                        }
                    />
                    <TextField
                        value={props.settings.tagsToAdd.length === 0 ? "" : props.settings.tagsToAdd[0]}
                        label="Tag to add"
                        placeholder="Enter tag name."
                        onChange={onTagToAddChange}
                        error={props.settingsErrors["tagsToAdd"]}
                        helpText={
                            <span>
                                The tag to add if the tag match condition is met.
                            </span>
                        }
                    />
                    <Checkbox
                        label="Auto remove tags?"
                        checked={props.settings.removeTagsIfRequiredTagIsMissing}
                        onChange={onRemoveTagsIfRequiredTagIsMissingChange}
                        helpText={
                            <span>
                                Enable automatic removal of 'Tag to add' when the tag match condition is no longer met.
                            </span>
                        }
                    />
                    <Checkbox
                        label="Skip Adding Tags If Already Present"
                        checked={props.settings.skipAddTagsIfAlreadyAdded}
                        onChange={onSkipAddTagsIfAlreadyAddedChange}
                        helpText={
                            <span>
                                Skips to add 'Tag to add' if it already exists.
                            </span>
                        }
                    />
                </FormLayout>
            </Card.Section>
            <Card.Section>
                <FormLayout>
                    <ChoiceList
                        allowMultiple
                        title="Apply the rule to products with the following states:"
                        choices={[
                            {
                                label: 'Active',
                                value: 'ACTIVE',
                            },
                            {
                                label: 'Archived',
                                value: 'ARCHIVED',
                            },
                            {
                                label: 'Draft',
                                value: 'DRAFT',
                            },
                        ]}
                        selected={props.settings.productStatuses}
                        onChange={onProductStatusesChange}
                    />
                </FormLayout>
            </Card.Section>
        </React.Fragment>
    );

}
